import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  HostListener,
  Input,
  Output,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { A11yModule } from '@angular/cdk/a11y';

let nextConfirmActionModalId = 0;

/**
 * The application's standard "confirm this action?" overlay: a header bar with a title and close
 * button, a short message body, and a Cancel / Confirm footer.
 *
 * Purely presentational — it owns no business logic and performs no data access. Feature modals
 * wrap it and keep their own inputs, outputs and domain rules (see e.g. DeletePackageModalComponent).
 *
 * Layout differences between consumers are expressed with CSS custom properties set on the host
 * so that each call site keeps its existing appearance:
 * `--confirm-modal-width`, `--confirm-modal-max-width`, `--confirm-modal-border`,
 * `--confirm-modal-shadow`, `--confirm-modal-overlay-padding`.
 *
 * Accessibility: renders a labelled `role="dialog"` with `aria-modal`, traps and auto-captures
 * focus while open (restoring it on close), closes on Escape, and marks the dialog `aria-busy`
 * while an action is in flight.
 *
 * @component
 * @selector app-confirm-action-modal
 * @example
 *   <app-confirm-action-modal
 *     title="Delete Package?"
 *     message="Are you sure you want to delete this package? This action cannot be undone."
 *     confirmLabel="Delete"
 *     (confirm)="onConfirm()"
 *     (cancel)="onClose()"
 *   ></app-confirm-action-modal>
 *
 * @input {string} title - Heading shown in the modal header.
 * @input {string} message - Body paragraph. Omit it and project content instead for a rich body.
 * @input {string} confirmLabel - Text of the primary (confirm) button.
 * @input {string} cancelLabel - Text of the secondary (cancel) button.
 * @input {string} cancelButtonClass - Style class of the Cancel button. Defaults to `btn-secondary`.
 * @input {boolean} confirmDisabled - Disables only the primary button (e.g. an invalid form); Cancel
 *   stays available. Defaults to false.
 * @input {boolean} busy - When true every control is disabled and the dialog is marked aria-busy.
 * @input {string} busyLabel - Primary button text while busy; falls back to confirmLabel.
 * @input {string} confirmButtonClass - Extra class(es) appended to the primary button.
 * @input {boolean} closeOnOverlayClick - Whether a backdrop click cancels. Defaults to true.
 * @input {boolean} mobileSheet - Whether the container becomes a bottom sheet under 768px.
 * @input {string} titleId - id used for aria-labelledby. Auto-generated when not supplied.
 * @input {string} testId - Root `data-testid`, and the stem for the child hooks
 *   `<testId>-title`, `<testId>-message`, `<testId>-close-button`, `<testId>-cancel-button` and
 *   `<testId>-confirm-button`. Defaults to the generic `confirmation-dialog`; consumers that can
 *   appear more than once on a page (e.g. the campaign table renders four of these) must pass a
 *   feature-scoped value so the hooks stay unique.
 * @output {EventEmitter<void>} confirm - The user activated the primary button.
 * @output {EventEmitter<void>} cancel - The user dismissed the dialog (close button, Cancel
 *   button, backdrop click or Escape). All four route here, matching every implementation this
 *   component replaces.
 *
 * Key methods:
 * - onConfirm(): emits `confirm` unless busy.
 * - onCancel(): emits `cancel` unless busy.
 * - onOverlayClick(event): emits `cancel` when the backdrop itself was clicked and
 *   `closeOnOverlayClick` is enabled.
 * - onEscape(): emits `cancel` on the Escape key unless busy.
 */
@Component({
  selector: 'app-confirm-action-modal',
  standalone: true,
  imports: [CommonModule, A11yModule],
  templateUrl: './confirm-action-modal.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./confirm-action-modal.component.scss'],
})
export class ConfirmActionModalComponent {
  @Input() title = '';
  @Input() message = '';
  @Input() confirmLabel = 'Confirm';
  @Input() cancelLabel = 'Cancel';
  @Input() confirmDisabled = false;
  @Input() busy = false;
  @Input() busyLabel = '';
  @Input() confirmButtonClass = '';
  @Input() cancelButtonClass = 'btn-secondary';
  /** False leaves only the primary button, for a notice with nothing to cancel. */
  @Input() showCancel = true;
  @Input() closeOnOverlayClick = true;
  @Input() mobileSheet = true;
  @Input() titleId = `confirm-action-modal-title-${(nextConfirmActionModalId += 1)}`;
  @Input() testId = 'confirmation-dialog';

  @Output() confirm = new EventEmitter<void>();
  @Output() cancel = new EventEmitter<void>();

  /** Primary button text, swapped for `busyLabel` while an action is in flight. */
  get primaryLabel(): string {
    if (this.busy && this.busyLabel) {
      return this.busyLabel;
    }
    return this.confirmLabel;
  }

  onConfirm(): void {
    if (!this.busy && !this.confirmDisabled) {
      this.confirm.emit();
    }
  }

  onCancel(): void {
    if (!this.busy) {
      this.cancel.emit();
    }
  }

  onOverlayClick(event: Event): void {
    if (this.closeOnOverlayClick && event.target === event.currentTarget) {
      this.onCancel();
    }
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    this.onCancel();
  }
}
